"use client"

import styled from "@emotion/styled"

import { FetchMenusQuery, GetFootersQuery } from "@eurodrive/apollo"
import { Culture, extractProperty, getNonUmbracoUrl } from "@eurodrive/utils"
import { getUmbracoMediaUrl } from "@eurodrive/utils/getUmbracoMediaUrl"
import { Button, IconButton } from "@mui/material"
import { SaleCarDtoPaginatedList, SearchMetadataResponse } from "api"
import Image from "next/image"
import Link from "next/link"
import { useState } from "react"
import Container from "../../Container"
import Close from "../../icons/Close"
import { Mobile } from "../../MediaQueries"
import { sharedColors } from "../../theme/colors"
import SideDrawer from "./Drawer"
import NavLink from "./NavLink"
import { SearchModal } from "./Search/SearchModal"

const StyledHeader = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  height: 64px;
  width: 100%;
  z-index: 1000000000;
  background-color: ${sharedColors.white};
  ${({ theme }) => theme.breakpoints.up("md")} {
    height: 72px;
  }
  box-shadow: 0px 2px 10px 0px #00000029;
`
const InnerHeader = styled.div`
  /* padding: 12px 0; */
  height: 100%;
  gap: 32px;
  display: flex;
  flex-direction: row-reverse;
  align-items: center;
  justify-content: flex-end;
  ${({ theme }) => theme.breakpoints.up("md")} {
    flex-direction: row;
  }
`

const LogoContainer = styled(Link)`
  position: relative;
  width: 117px;
  height: 32px;
  margin-left: auto;
  ${({ theme }) => theme.breakpoints.up("md")} {
    margin-left: unset;
    margin-right: auto;
    width: 146px;
    height: 40px;
  }
  :focus-visible {
    border: 1px dashed white;
    outline-offset: 2px;
    outline: 2px solid black;
  }
`
const IconsBox = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`

const StyledMenu = styled.nav`
  display: none;
  ${({ theme }) => theme.breakpoints.up("md")} {
    display: flex;
    align-items: center;
    gap: 16px;
    height: 100%;
  }
  ${({ theme }) => theme.breakpoints.up("xl")} {
    justify-content: center;
    gap: 32px;
    flex: 1;
  }
`

const StyledButton = styled(Button)<{ target: string }>`
  ${({ theme }) => theme.breakpoints.down("md")} {
    display: none;
  }
`

const StyledMenuIcon = styled(IconButton)`
  padding: 0;
  ${({ theme }) => theme.breakpoints.up("md")} {
    display: none;
  }
  :focus-visible {
    border: 1px dashed white;
    outline-offset: 2px;
    outline: 2px solid black;
  }
`

const StyledIconButton = styled(IconButton)<{ target?: string; href?: string }>`
  padding: 0;
  :focus-visible {
    border: 1px dashed white;
    outline-offset: 2px;
    outline: 2px solid black;
  }
`
const createWaMeLink = function (
  company?: string,
  model?: string,
  color?: string,
  websiteLink?: string,
) {
  const baseLink = "https://wa.me/972547171721?text="

  const parts = [
    `אשמח לשוחח עם נציג לגבי ${company} ${model}`,
    color ? `בצבע ${color}` : null,
    websiteLink ? `שראיתי באתר שלכם בקישור ${websiteLink}` : "שראיתי באתר שלכם",
  ]

  const systemString = parts.filter(Boolean).join(" ")
  return baseLink + encodeURIComponent(systemString)
}
// Define types for the transformed menu data
interface SubMenuItemData {
  label: string
  href: string
}

export interface MenuItemData {
  label: string
  href: string
  items: SubMenuItemData[]
}

export default function Header({
  locale,
  menu_data,
  footer_data,
  searchCars,
  metadata,
}: {
  locale: string
  menu_data: FetchMenusQuery
  footer_data: GetFootersQuery
  searchCars: SaleCarDtoPaginatedList
  metadata: SearchMetadataResponse
}) {
  // Header Data
  const culture: Culture = locale === "en" ? "en-us" : "he-il"
  const [modalOpen, setModalOpen] = useState(false)
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [openMenuIndex, setOpenMenuIndex] = useState<number | null>(null)
  const toggleDrawer =
    (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
      if (
        event.type === "keydown" &&
        ((event as React.KeyboardEvent).key === "Tab" ||
          (event as React.KeyboardEvent).key === "Shift")
      ) {
        return
      }
      setIsMenuOpen(open)
    }
  const headerData = footer_data.header
  const logo = extractProperty(headerData, "logo")
  const footer = footer_data.footer
  const socialItems = extractProperty(footer, "socialItems") || []
  const socialLinks = socialItems.map((el: any) => ({
    icon: extractProperty(el, "icon"),
    url: extractProperty(el, "link")?.[0]?.url,
    name: extractProperty(el, "link")?.[0]?.name,
  })) as {
    icon: string
    url: string
    name: string
  }[]
  // The financing CTA is optional in Umbraco — when the editor leaves the
  // link picker empty there is nothing to link to, so the button is dropped
  // from the header entirely rather than rendered blank. Gate on the url
  // alone: a picked link can legitimately come back with a null name (see
  // whatsAppLink on the same doc type), and those must keep rendering as
  // they do today.
  const headerButton = extractProperty(headerData, "button")?.[0]
  const buttonText = headerButton?.name
  const buttonLink = headerButton?.url
  const buttonType = headerButton?.type
  // Transform menu data using direct index access
  const menusSource = menu_data.menu_desktop?.children || []
  const menusmap: MenuItemData[] = menusSource.map((el: any) => {
    // Consider using generated types instead of 'any'
    const label = el.properties?.[0]?.property?.menuName || ""
    const href = el.properties?.[1]?.property?.menuTarget?.[0]?.url
      ? getNonUmbracoUrl(el.properties[1].property.menuTarget[0].url)
      : el.properties?.[1]?.property?.menuName

    const children = el.children || []
    const items: SubMenuItemData[] = children.map((i: any) => {
      const childLabel = i.properties?.[0]?.property?.menuName || ""
      const childHref = i.properties?.[1]?.property?.menuTarget?.[0]?.url
        ? getNonUmbracoUrl(i.properties[1].property.menuTarget[0].url)
        : i.properties?.[1]?.property?.menuName
      return { label: childLabel, href: childHref }
    })

    return { label, href, items }
  })
  const handleCloseModal = () => setModalOpen(false)
  return (
    // mui-fixed: MUI's scroll lock (nav Menu, search Modal) pads this fixed
    // header by the scrollbar width, so its content doesn't shift sideways.
    <StyledHeader className="mui-fixed">
      <Container paddingMobile heightFull>
        <InnerHeader>
          {/* LOGO */}
          <LogoContainer href="/">
            <Image
              alt="euro drive עמוד הבית"
              src={getUmbracoMediaUrl(logo)}
              fill
              priority
            />
          </LogoContainer>

          {/* MENU */}
          <StyledMenu>
            {menusmap.map((menu, index) => (
              <NavLink
                key={`${menu.label}-${index}`}
                data={menu}
                isOpen={openMenuIndex === index}
                onToggle={() => setOpenMenuIndex(index)}
                onClose={() => setOpenMenuIndex(null)}
              />
            ))}
          </StyledMenu>

          {/* ACTIONS */}
          <IconsBox>
            <Mobile>
              {isMenuOpen ? (
                <IconButton onClick={() => setIsMenuOpen(false)}>
                  <Close />
                </IconButton>
              ) : (
                <StyledMenuIcon onClick={() => setIsMenuOpen(true)}>
                  <Image
                    alt=""
                    src={"/icons/kebab.svg"}
                    width={24}
                    height={24}
                    priority
                  />
                </StyledMenuIcon>
              )}
              <SideDrawer
                isOpen={isMenuOpen}
                toggleDrawer={toggleDrawer}
                setIsDrawerOpen={setIsMenuOpen}
                menusmap={menusmap}
                locale={locale}
                socialLinks={socialLinks}
              />
            </Mobile>
            <StyledIconButton
              aria-expanded={modalOpen}
              onClick={() => setModalOpen(true)}
            >
              <Image
                alt="רכיב חיפוש"
                src={"/icons/search.svg"}
                width={24}
                height={24}
                priority
              />
            </StyledIconButton>
          </IconsBox>
          {/* BUTTON */}
          {buttonLink ? (
            <StyledButton
              variant="primary"
              LinkComponent={Link}
              size="large"
              href={getNonUmbracoUrl(buttonLink)}
              target={buttonType === "EXTERNAL" ? "_blank" : "_self"}
            >
              {buttonText}
            </StyledButton>
          ) : null}
        </InnerHeader>
      </Container>

      <SearchModal
        open={modalOpen}
        handleClose={handleCloseModal}
        searchCars={searchCars}
        metadata={metadata}
      />
    </StyledHeader>
  )
}
